import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";

import AppNavigator from "./router/AppNavigator";
import LoginNavigator from "./router/LoginNavigator";
import { AppDispatch, AppState } from "./store/configureStore";
import AdminNavigator from "./router/AdminNavigator";
import { externalSignIn, handleRedirect, setAuthProvider } from "./store/users/auth";
import { roles } from "./util/config/roles";

import { enGB as en } from "date-fns/locale";
import { et } from "date-fns/locale/et";
import { lt } from "date-fns/locale/lt";
import { lv } from "date-fns/locale/lv";
import { ru } from "date-fns/locale/ru";
import i18n from "i18n";
import { registerLocale } from "react-datepicker";
import { KeycloakAuthProvider } from "./store/users/authProviders/KeycloakAuthProvider";
import { MsalAuthProvider } from "./store/users/authProviders/MsalAuthProvider";
import { initializeMsal, msalInstance } from "util/msal/config";

import { useConfig } from "ConfigContext";
declare global {
    interface Window {
        _env_?: any;
    }
}


const App = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [isLogged, setIsLogged] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const role = roles();

    const { isLoggedIn, loading } = useSelector((state: AppState) => state.users.auth);

    //setAuthProvider(new KeycloakAuthProvider());

    switch (i18n.language) {
        case "en":
            registerLocale("en", en);
            break;
        case "et":
            registerLocale("et", et);
            break;
        case "lt":
            registerLocale("lt", lt);
            break;
        case "lv":
            registerLocale("lv", lv);
            break;
        case "ru":
            registerLocale("ru", ru);
            break;
        default:
            registerLocale("en", en);
    }

    useEffect(() => {
        const handleLoad = () => document.body.classList.add("loaded");
        window.addEventListener("load", handleLoad);
        document.body.classList.add("loaded");
    }, []);

    useEffect(() => {
        setIsLogged(isLoggedIn);
    }, [isLoggedIn]);

    useEffect(() => {
        const initAuth = async () => {
            try {
                await initializeMsal();
                const msalResponse = await msalInstance.handleRedirectPromise();
                const urlParams = new URLSearchParams(window.location.search);
                const isKeycloakRedirect = urlParams.get('code') || urlParams.has('state') || urlParams.has('session_state');
                if (msalResponse) {
                    console.log("MSAL redirect handled successfully!");
                    setAuthProvider(new MsalAuthProvider());
                    msalInstance.setActiveAccount(msalResponse.account);
                    if (msalResponse.idToken) {
                        dispatch(externalSignIn(msalResponse.idToken));
                    }
                } else if (isKeycloakRedirect) {
                    console.log("Keycloak redirect detected");
                    setAuthProvider(new KeycloakAuthProvider());
                    dispatch(handleRedirect());
                } else if (isLoggedIn) {
                    if (msalInstance.getAllAccounts().length > 0) {
                        setAuthProvider(new MsalAuthProvider());
                    } else {
                        setAuthProvider(new KeycloakAuthProvider());
                    }
                } else {
                    console.log("Fresh load, no redirects");
                }
            } catch (error) {
                console.error("Auth Initialization Error:", error);
            } finally {
                setIsAuthReady(true);
            }
        };

        initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    if (!isAuthReady) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <ToastContainer />
            {isLogged && !loading ? (
                role.isSuperAdminOrAdmin ? (
                    <AdminNavigator />
                ) : (
                    // <ConfirmationGate>
                        <AppNavigator />
                    // </ConfirmationGate>
                )
            ) : (
                <LoginNavigator />
            )}
        </>
    );
};

export default App;
